import * as THREE from "three";
import { AttachmentPoint } from "./AttachmentPoint.js";
import {
  isModuleToModuleConnection,
  getActiveAttachmentPoints,
  resolveCollisionSpec,
  isPointInsideFurnitureXZ,
  applyMirror,
  disposeMesh,
  collisionBoxesUnion,
} from "../utils/geometry.js";
import { LEG_HEIGHT_TWEEN_MS } from "./element/tweenTiming.js";
import { moduleAdjacencyMethods } from "./element/moduleAdjacency.js";
import { legHeightStatics, legHeightMethods } from "./element/legHeight.js";
import { variantTweenStatics, variantTweenMethods } from "./element/variantTween.js";
import { variantSwitchMethods } from "./element/variantSwitch.js";
import { installMethods } from "../utils/installMethods.js";

export class FurnitureElement {
  static LEG_HEIGHT_TWEEN_MS = LEG_HEIGHT_TWEEN_MS;

  constructor(data) {
    this.id = data.id || THREE.MathUtils.generateUUID();
    this.type = data.type;
    this.name = data.name;
    this.mesh = null;
    this.originalMesh = data.mesh; // Store reference to loaded mesh
    this.compositeMesh = null; // Embedded sub-model that animates between variants (e.g. swinging armrest)
    this.scene = data.scene;
    this.markerRoot = data.markerRoot ?? data.scene; // Where the attachment-point pluses are drawn
    this.initialPosition = data.position; // Store initial position to avoid flash at origin
    this.boundingBox = null;
    // Array of {size: Vector3, offset: Vector3} for multi-box collision (L-shaped pieces).
    // null = single-box mode (most furniture). Set by computeBoundingBoxFull() when
    // furnitureData.properties.collisionBoxes is present.
    this.collisionBoxes = null;
    this.fabricId = null;
    this.price = data.price || 0;
    this.isSelected = false;
    this.furnitureData = data; // Store full furniture data
    this.variantIndex = data.variantIndex || 0;

    // Attachment system
    this.attachmentPoints = [];
    this.attachedTo = null; // Parent attachment point
    this.attachedChildren = []; // Child furniture attached to this
    this.moduleConnectionSide = null; // Which of THIS module's local sides connects to the parent module

    // Named child nodes referenced by variant.nodeAnimations.
    // Map<nodeName, { node, bindPos: Vector3, bindQuat: Quaternion }> populated in setupMesh.
    this._animatedNodes = new Map();

    // Active variant transition tween (composite transform + per-node transforms),
    // multi-stage transitions chained through nextTween. See _startTweenChain().
    // Shape: { elapsed, duration, composite?, nodes:[], components?, pendingList?, nextTween? }
    this._tween = null;

    // Leg-height tween runs independently of _tween so a concurrent variant
    // transition doesn't steal its progress. See applyLegHeight().
    this._legHeightTween = null;
    this._legBodyLiftY = 0;
    this._legGroups = null;
    this._legScaleAxis = 'y';

    // Selection outline (managed centrally by FurnitureManager)

    this.init();
  }

  init() {
    if (this.originalMesh) {
      this.setupMesh();
      this.computeBoundingBoxFull();
      this.createAttachmentPoints();
    }
  }

  createAttachmentPoints() {
    const attachmentPointsData = getActiveAttachmentPoints(this.furnitureData, this.variantIndex);

    for (const pointData of attachmentPointsData) {
      // Validate attachment point data
      if (!this.validateAttachmentPointData(pointData)) {
        console.warn(`Invalid attachment point data for furniture ${this.id}:`, pointData);
        continue;
      }

      const offset = new THREE.Vector3(
        pointData.offset.x,
        pointData.offset.y,
        pointData.offset.z
      );

      const attachmentPoint = new AttachmentPoint(
        this,
        pointData.side,
        offset,
        pointData.accepts,
        pointData.anchorType ?? null,
        pointData.compatibilityTag ?? 0,
        pointData.facingAngle ?? null
      );

      if (pointData.rotationYOffset) {
        attachmentPoint.localRotationY += pointData.rotationYOffset;
      }

      if (pointData.requiresAdjacentModule) {
        attachmentPoint.requiresAdjacentModule = pointData.requiresAdjacentModule;
      }
      if (pointData.requiresAdjacentModuleOnSide) {
        attachmentPoint.requiresAdjacentModuleOnSide = pointData.requiresAdjacentModuleOnSide;
      }
      if (pointData.requiresAdjacentModuleOnSides) {
        // Every listed side must carry a required module that reaches this AP —
        // used for corner pillow slots (bard E elements) that only exist where two
        // backrest modules meet.
        attachmentPoint.requiresAdjacentModuleOnSides = pointData.requiresAdjacentModuleOnSides;
      }
      if (pointData.fixed) {
        // Pin the attached component to the AP origin — no sliding along the
        // parent face (used for the diagon angled pillows).
        attachmentPoint.fixed = true;
      }
      if (pointData.connectionOffsetByUrlCode) {
        // Per-cushion placement offset on THIS AP, keyed by urlCode. Overrides
        // the cushion's own properties.connectionOffset, so mirrored slots can
        // place the same pillow at mirror-correct offsets.
        attachmentPoint.connectionOffsetByUrlCode = pointData.connectionOffsetByUrlCode;
      }
      if (pointData.requiresFlankingCushions) {
        attachmentPoint.requiresFlankingCushions = pointData.requiresFlankingCushions;
        // Search radius (m) for a flanking cushion measured from the AP world
        // position; defaults to a tight gap so only cushions touching the slot
        // qualify. Tune per collection in furniture.json.
        attachmentPoint.flankRadius = pointData.flankRadius ?? 0.6;
      }
      if (pointData.acceptsOnly) {
        attachmentPoint.acceptsOnly = pointData.acceptsOnly;
      }
      if (pointData.requiresParent) {
        attachmentPoint.requiresParent = pointData.requiresParent;
      }
      if (pointData.rotationY !== undefined) {
        attachmentPoint.componentRotationY = THREE.MathUtils.degToRad(pointData.rotationY);
      }
      // After rotationY: a range runs along the seated component's length.
      if (Array.isArray(pointData.range) && !pointData.fixed) {
        attachmentPoint.setRange(pointData.range);
      }

      this.attachmentPoints.push(attachmentPoint);

      // Outside the furniture's graph; updateAttachmentPoints() places it.
      this.markerRoot?.add(attachmentPoint.getMesh());
    }
  }

  /**
   * Validates attachment point data structure
   * @param {Object} pointData - The attachment point data to validate
   * @returns {boolean} - True if data is valid
   */
  validateAttachmentPointData(pointData) {
    if (!pointData) return false;
    if (!pointData.side || typeof pointData.side !== 'string') return false;
    if (!pointData.offset || typeof pointData.offset !== 'object') return false;
    if (typeof pointData.offset.x !== 'number') return false;
    if (typeof pointData.offset.y !== 'number') return false;
    if (typeof pointData.offset.z !== 'number') return false;
    if (!Array.isArray(pointData.accepts)) return false;
    return true;
  }

  /** Offer a plus on every free attachment point the placement rules allow, selected or not. */
  refreshAttachmentPointVisibility() {
    this.attachmentPoints.forEach((point) => {
      if (!point.isOccupied && this.shouldShowAttachmentPoint(point)) {
        // Show first (sets visible flag), then immediately update position
        point.show();
        point.update();
      } else {
        point.hide();
      }
    });
  }

  /**
   * Clear all attachment points on this furniture that are occupied by a specific furniture
   * @param {FurnitureElement} furniture - The furniture to clear attachment points for
   * @param {boolean} moduleOnly - If true, only clear module attachment points
   */
  clearAttachmentPointsOccupiedBy(furniture, moduleOnly = false) {
    this.attachmentPoints.forEach(ap => {
      if (ap.holds(furniture)) {
        if (!moduleOnly || ap.acceptsComponentTypes.includes("module")) {
          ap.setUnoccupied(furniture);
        }
      }
    });
  }

  /** Per frame: re-place markers on world-transform change; draw those `dragged` can sit on while drawn, ranged per `runsFor`. */
  updateAttachmentPoints(dragged = null, onlyAP = null, runsFor = null) {
    let shown = true;
    for (let o = this.mesh; o; o = o.parent) {
      if (!o.visible) { shown = false; break; }
    }

    this.mesh.updateWorldMatrix(true, false);
    const matrixWorld = this.mesh.matrixWorld;
    const moved = !this._markerMatrix?.equals(matrixWorld);
    if (moved) (this._markerMatrix ??= new THREE.Matrix4()).copy(matrixWorld);

    for (const point of this.attachmentPoints) {
      const wanted = onlyAP ? point === onlyAP : dragged !== null && point.seats(dragged);
      // A ranged AP draws a plus per stretch the dragged piece still fits in.
      if (wanted && runsFor && point.range && point.visible) point.setRuns(runsFor(point, dragged));
      if (moved && point.visible) point.update(matrixWorld);
      point.setDrawn(shown, wanted);
    }
  }

  attachTo(attachmentPoint, childConnectionSide = null) {
    this.attachedTo = attachmentPoint;
    attachmentPoint.setOccupied(this);
    attachmentPoint.parentFurniture.attachedChildren.push(this);

    const parentFurniture = attachmentPoint.parentFurniture;
    const parentMesh = parentFurniture.getMesh();

    // Determine if this should be a scene-graph child or just a reference connection
    // Modules connecting to modules stay as scene children (siblings)
    // Components (panels, backrests, cushions) become actual children
    const isModuleConnection = isModuleToModuleConnection(this, parentFurniture);

    if (isModuleConnection) {
      // Store which local side of THIS module faces the parent — used by
      // hasAdjacentModuleOnSide (Case 1) to know which panel APs to hide.
      // Must be set before refreshAttachmentPointVisibility is called below.
      this.moduleConnectionSide = childConnectionSide;

      parentMesh.updateMatrixWorld(true);
      this.mesh.updateMatrixWorld(true);
      const apWorldPos = new THREE.Vector3();

      // Mark ALL child APs on the connection side as occupied by the parent module.
      // Using filter (not find) ensures every AP on this face is blocked so that
      // another module cannot snap to the child's connection face (the bug where
      // a second R2 would snap to R2-1's front instead of to E1).
      // An exactCollisionBox piece keeps the stretch its parent doesn't cover: a bard
      // armrest runs 25 cm past the seat it closes, and a backrest's end seats there.
      // Its snap gate (CollisionSystem.EXACT_BOX_PENETRATION) rejects anything sinking in.
      if (childConnectionSide) {
        const exact = this.furnitureData?.properties?.exactCollisionBox;
        this.attachmentPoints
          .filter(ap => ap.side === childConnectionSide && ap.acceptsComponentTypes.includes("module"))
          .forEach(ap => {
            if (exact) {
              ap.getWorldPosition(apWorldPos);
              if (!isPointInsideFurnitureXZ(parentFurniture, apWorldPos.x, apWorldPos.z, 0.02)) return;
            }
            ap.setOccupied(parentFurniture);
          });
      }

      // Mark only parent APs on this side that the child's footprint actually covers.
      // The snapped AP is always marked; siblings are tested against the child's world
      // footprint so a small child doesn't block a large parent's other free APs.
      // SnapSystem.findClosestModuleAttachment has a runtime physically-blocked guard
      // (isPointInsideFurnitureXZ) so leaving uncovered APs free is safe.
      const parentSide = attachmentPoint.side;
      parentFurniture.attachmentPoints.forEach(ap => {
        if (ap.side !== parentSide) return;
        if (!ap.acceptsComponentTypes.includes("module")) return;
        if (ap.isOccupied) return;
        if (ap === attachmentPoint) { ap.setOccupied(this); return; }
        ap.getWorldPosition(apWorldPos);
        if (isPointInsideFurnitureXZ(this, apWorldPos.x, apWorldPos.z, 0.02)) {
          ap.setOccupied(this);
        }
      });

      // Module-to-module: Keep as scene child, just track reference
      // This allows independent deletion - deleting one module doesn't delete connected modules
      // The mesh stays in scene root, position/rotation stay in world space
      // Connection is tracked via attachedTo and attachedChildren references only

      // Refresh ALL modules in the group after connecting — sibling modules' panel AP visibility
      // depends on hasAdjacentModuleOnSide which changes whenever the group composition changes
      const group = parentFurniture.getAllGroupModules();
      group.forEach(m => m.refreshAttachmentPointVisibility());
      this.refreshAttachmentPointVisibility();

      return;
    }

    // Component attachment: Make this a real child in scene graph
    // (Panels, backrests, cushions should be deleted with their parent)
    const worldPos = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    this.mesh.getWorldPosition(worldPos);
    this.mesh.getWorldQuaternion(worldQuat);

    // Remove from scene (if it's in the scene)
    if (this.mesh.parent) {
      this.mesh.parent.remove(this.mesh);
    }

    // Add to parent mesh
    parentMesh.add(this.mesh);

    // Convert world transform back to local space relative to new parent
    parentMesh.worldToLocal(worldPos);
    this.mesh.position.copy(worldPos);

    const parentWorldQuat = new THREE.Quaternion();
    parentMesh.getWorldQuaternion(parentWorldQuat);
    parentWorldQuat.invert();
    worldQuat.premultiply(parentWorldQuat);
    this.mesh.quaternion.copy(worldQuat);

    // Override Y rotation when the AP declares an explicit componentRotationY —
    // this ensures cushions face the correct direction regardless of drag orientation.
    if (attachmentPoint.componentRotationY !== undefined) {
      this.mesh.rotation.y = attachmentPoint.componentRotationY;
    }

    // Refresh parent's attachment point visibility — adding a component may change
    // which remaining attachment points are still valid (canAttachPanel rules, etc.)
    parentFurniture.refreshAttachmentPointVisibility();
  }

  /**
   * Clear attachment references between this furniture and its parent
   * @param {FurnitureElement} parentFurniture - The parent furniture
   */
  clearAttachmentReferences(parentFurniture) {
    // Clear ALL parent attachment points occupied by this child
    parentFurniture.clearAttachmentPointsOccupiedBy(this);

    // Remove this from parent's children array
    const index = parentFurniture.attachedChildren.indexOf(this);
    if (index !== -1) {
      parentFurniture.attachedChildren.splice(index, 1);
    }
  }

  /**
   * Handle detachment of module-to-module connection
   * @param {FurnitureElement} parentFurniture - The parent furniture
   */
  detachModuleConnection(parentFurniture) {
    // Clear ALL attachment points on this child that are occupied by the parent
    // This handles rotated modules correctly
    this.clearAttachmentPointsOccupiedBy(parentFurniture, true);

    // Modules stay in scene root, no reparenting needed
    this.attachedTo = null;
    this.moduleConnectionSide = null;

    // Refresh ALL remaining modules in parent's group (not just the direct parent).
    // Sibling modules that are still connected to parentFurniture may have stale
    // hasAdjacentModuleOnSide results — their panel APs must be recalculated now
    // that this module has left the group.
    const remainingGroup = parentFurniture.getAllGroupModules();
    remainingGroup.forEach(m => m.refreshAttachmentPointVisibility());

    this.refreshAttachmentPointVisibility();
  }

  /**
   * Handle detachment of component (panel, backrest, cushion) from parent
   * @param {FurnitureElement} parentFurniture - The parent furniture
   * @param {THREE.Scene} scene - The scene to reparent to
   */
  detachComponentConnection(parentFurniture, scene) {
    // Reparent back to scene
    if (scene) {
      // Convert to world space before reparenting
      const worldPos = new THREE.Vector3();
      const worldQuat = new THREE.Quaternion();
      this.mesh.getWorldPosition(worldPos);
      this.mesh.getWorldQuaternion(worldQuat);

      // Remove from parent
      if (this.mesh.parent) {
        this.mesh.parent.remove(this.mesh);
      }

      // Add back to scene
      scene.add(this.mesh);

      // Apply world transform
      this.mesh.position.copy(worldPos);
      this.mesh.quaternion.copy(worldQuat);
    }

    this.attachedTo = null;

    // Refresh parent's attachment point visibility
    if (parentFurniture) {
      parentFurniture.refreshAttachmentPointVisibility();
    }

    // Refresh child's attachment point visibility
    this.refreshAttachmentPointVisibility();
  }

  detachFrom(scene) {
    if (!this.attachedTo) return;

    const attachedAP = this.attachedTo;
    const parentFurniture = attachedAP.parentFurniture;
    const isModuleConnection = isModuleToModuleConnection(this, parentFurniture);

    // Explicitly free the primary attachment point (belt-and-suspenders —
    // clearAttachmentReferences also does this, but ensuring the specific
    // attachedTo AP is freed prevents stale occupation after detach).
    if (attachedAP.holds(this)) {
      attachedAP.setUnoccupied(this);
    }

    // Clear attachment references
    this.clearAttachmentReferences(parentFurniture);

    // Handle specific connection type
    if (isModuleConnection) {
      this.detachModuleConnection(parentFurniture);
    } else {
      this.detachComponentConnection(parentFurniture, scene);
    }
  }

  setupMesh() {
    // Clone the original mesh to allow multiple instances
    this.mesh = this.originalMesh.clone();
    this.mesh.userData.furnitureElement = this;
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;

    // Set initial position immediately after cloning to avoid flash at origin
    if (this.initialPosition) {
      this.mesh.position.copy(this.initialPosition);
    }

    // Mirror the geometry, preserving the object's center so the mirrored piece
    // occupies the same footprint regardless of node orientation. `mirror` holds
    // the local axis to flip (see applyMirror).
    if (this.furnitureData?.properties?.mirror) {
      applyMirror(this.mesh, this.furnitureData.properties.mirror);
    }

    const compositeGroupName = this.furnitureData?.properties?.compositeGroupName;
    if (compositeGroupName) {
      this.mesh.traverse((child) => {
        if (!this.compositeMesh && child.name === compositeGroupName) {
          this.compositeMesh = child;
        }
      });
      if (this.compositeMesh) {
        this.compositeMesh.userData.furnitureElement = this;
        this._applyVariantCompositeTransform(this.variantIndex);
      }
    }

    // Ensure materials are cloned for individual texture application
    this.mesh.traverse((child) => {
      if (child.isMesh) {
        // Set userData for click detection
        child.userData.furnitureElement = this;

        if (Array.isArray(child.material)) {
          child.material = child.material.map((mat) => {
            if (mat && typeof mat.clone === 'function') {
              return mat.clone();
            }
            console.warn('Invalid material found on mesh:', child.name);
            return mat;
          });
        } else if (child.material && typeof child.material.clone === 'function') {
          child.material = child.material.clone();
        }
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    // Snapshot bind-pose for every node referenced by any variant's nodeAnimations.
    // Variants without an entry for a node revert it to its bind pose on switch.
    this._collectAnimatedNodes();
    this._applyVariantNodeStateInstant(this.variantIndex);
  }


  /**
   * Build this.boundingBox from a mesh-LOCAL collision box (offset + size).
   *
   * min/max/center are emitted in WORLD space: the 8 local corners are transformed
   * by the mesh's world matrix, so the element's own rotation AND any parent it has
   * been attached to are accounted for. This matters for reparented components —
   * e.g. a cushion seated on a rotated module, whose `mesh.position` is parent-LOCAL
   * — so the world-space consumers (room-bounds clamping, AP-persistence checks)
   * compare in a consistent frame.
   *
   * size/localCenterOffset stay in the mesh's LOCAL frame, since the rotation-aware
   * sub-box collision paths (CollisionSystem._buildChild/ParentSubBoxes) and the
   * debug overlay apply the mesh rotation themselves.
   */
  _assignBoundingBoxFromLocalBox(off, sz) {
    this.mesh.updateWorldMatrix(true, true);
    // Box3.applyMatrix4 transforms all 8 local corners, so this is the world AABB
    // accounting for the mesh's own rotation and any parent it's attached to.
    const worldBox = new THREE.Box3()
      .setFromCenterAndSize(off, sz)
      .applyMatrix4(this.mesh.matrixWorld);
    this.boundingBox = {
      min: worldBox.min,
      max: worldBox.max,
      size: sz,
      center: worldBox.getCenter(new THREE.Vector3()),
      localCenterOffset: off,
    };
    this._recordBoundingBoxOrigin();
  }

  // World origin the current box was computed at; updateBoundingBoxPosition() shifts by the delta from it.
  _recordBoundingBoxOrigin() {
    this._bbOrigin ??= new THREE.Vector3();
    this.mesh.getWorldPosition(this._bbOrigin);
  }

  /**
   * Full bounding-box recompute. Falls back to a geometry AABB (own meshes only,
   * excluding attached child elements) when nothing is authored.
   * Call after spawn, rotation, or variant switch.
   */
  computeBoundingBoxFull() {
    if (!this.mesh) return;

    const spec = resolveCollisionSpec(this.furnitureData?.properties, this.variantIndex);

    // --- Multi-box path ---
    if (spec?.boxes?.length > 0) {
      this.collisionBoxes = spec.boxes.map(cb => ({
        size:   new THREE.Vector3(cb.size.x,         cb.size.y,         cb.size.z),
        offset: new THREE.Vector3(cb.offset?.x ?? 0, cb.offset?.y ?? 0, cb.offset?.z ?? 0),
      }));

      // Union AABB across all sub-boxes for room-bounds clamping and fast collision rejection
      const u = collisionBoxesUnion(this.collisionBoxes);
      const sz  = new THREE.Vector3(u.maxX - u.minX, u.maxY - u.minY, u.maxZ - u.minZ);
      const off = new THREE.Vector3((u.minX + u.maxX) / 2, (u.minY + u.maxY) / 2, (u.minZ + u.maxZ) / 2);
      this._assignBoundingBoxFromLocalBox(off, sz);
      return;
    }
    this.collisionBoxes = null;

    // --- Single authored-box path ---
    if (spec?.box?.size) {
      const authored = spec.box;
      const sz  = new THREE.Vector3(authored.size.x,         authored.size.y,         authored.size.z);
      const off = new THREE.Vector3(authored.offset?.x ?? 0, authored.offset?.y ?? 0, authored.offset?.z ?? 0);
      this._assignBoundingBoxFromLocalBox(off, sz);
      return;
    }

    // --- Fallback: compute from mesh geometry ---
    // Two consumers need two different frames:
    //   • min/max/center are read in WORLD space (e.g. room-bounds clamping), so keep the world AABB.
    //   • localCenterOffset/size feed the rotation-aware collision paths
    //     (CollisionSystem sub-boxes, EventManager.checkComponentCollision) and the debug overlay,
    //     which treat the offset as mesh-LOCAL and apply rotation themselves.
    // setFromObject is world-space; for a component reparented under its module it
    // includes the parent's pose, so the old `worldCenter - this.mesh.position`
    // (a LOCAL position) was a garbage offset — the cause of the scattered boxes.
    // Bound ONLY this element's own geometry. setFromObject(this.mesh) would also
    // include attached child elements (e.g. a backrest's seated cushion + that
    // cushion's accessory, which are reparented under this.mesh). When the boxes are
    // recomputed after the scene graph is assembled (FurnitureManager recenter/load),
    // that inflates the parent's AABB to swallow its children, causing false
    // collisions with neighbours. Every mesh carries userData.furnitureElement, so we
    // skip any subtree belonging to a different element.
    this.mesh.updateWorldMatrix(true, true);
    const invWorld = this.mesh.matrixWorld.clone().invert();
    const worldBox = new THREE.Box3();   // this element's geometry, world space
    const localBox = new THREE.Box3();   // same, in this mesh's local frame
    const childWorld = new THREE.Box3();
    const childLocal = new THREE.Box3();
    const toLocal = new THREE.Matrix4();
    this.mesh.traverse((child) => {
      if (!child.isMesh || !child.geometry) return;
      const owner = child.userData?.furnitureElement;
      if (owner && owner !== this) return; // belongs to an attached child element
      if (!child.geometry.boundingBox) child.geometry.computeBoundingBox();
      childWorld.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
      worldBox.union(childWorld);
      toLocal.multiplyMatrices(invWorld, child.matrixWorld);
      childLocal.copy(child.geometry.boundingBox).applyMatrix4(toLocal);
      localBox.union(childLocal);
    });

    // worldBox and localBox are unioned in lockstep (every kept child feeds both),
    // so they are empty together — a single guard covers all four derived values.
    let worldCenter, off, sz, half;
    if (worldBox.isEmpty()) {
      worldCenter = this.mesh.getWorldPosition(new THREE.Vector3());
      off = new THREE.Vector3();
      sz = new THREE.Vector3();
      half = new THREE.Vector3();
    } else {
      worldCenter = worldBox.getCenter(new THREE.Vector3());
      off = localBox.getCenter(new THREE.Vector3());
      sz = localBox.getSize(new THREE.Vector3());
      half = worldBox.getSize(new THREE.Vector3()).multiplyScalar(0.5);
    }

    this.boundingBox = {
      min: worldCenter.clone().sub(half),
      max: worldCenter.clone().add(half),
      size: sz,
      center: worldCenter.clone(),
      localCenterOffset: off.clone(),
    };
    this._recordBoundingBoxOrigin();
  }

  /**
   * Cheap bounding-box update after a pure translation: the world box keeps its
   * rotated extents and shifts by however far the mesh origin moved since it was computed.
   * Falls back to a full recompute if there's no existing bounding box.
   */
  updateBoundingBoxPosition() {
    if (!this.mesh) return;
    if (!this.boundingBox || !this._bbOrigin) {
      this.computeBoundingBoxFull();
      return;
    }

    this._bbDelta ??= new THREE.Vector3();
    this.mesh.updateWorldMatrix(true, false);
    this._bbDelta.setFromMatrixPosition(this.mesh.matrixWorld).sub(this._bbOrigin);
    this._bbOrigin.add(this._bbDelta);
    this.boundingBox.center.add(this._bbDelta);
    this.boundingBox.min.add(this._bbDelta);
    this.boundingBox.max.add(this._bbDelta);
  }

  /**
   * @deprecated Use computeBoundingBoxFull() or updateBoundingBoxPosition() directly.
   * Kept for backward-compatibility with external call sites that still use this name.
   */
  calculateBoundingBox() {
    this.computeBoundingBoxFull();
  }

  setPosition(x, y, z) {
    if (!this.mesh) return;
    this.mesh.position.set(x, y, z);
    this.updateBoundingBoxPosition();
  }

  // Translate on XZ so the visual (bounding-box) center lands at (x, z). Shifts the
  // already-computed box by the pure-translation delta — caller must have a current box.
  setCenterXZ(x, z) {
    if (!this.mesh || !this.boundingBox) return;
    const dx = x - this.boundingBox.center.x;
    const dz = z - this.boundingBox.center.z;
    this.mesh.position.x += dx;
    this.mesh.position.z += dz;
    const { center, min, max } = this.boundingBox;
    center.x = x; center.z = z;
    min.x += dx; max.x += dx;
    min.z += dz; max.z += dz;
    if (this._bbOrigin) this._recordBoundingBoxOrigin();
  }

  rotate(angleY) {
    if (!this.mesh) return;
    this.mesh.rotateY(angleY);
    this.computeBoundingBoxFull();
  }

  setSelected(selected) {
    this.isSelected = selected;
  }

  /**
   * Advance the leg-height and variant tweens each frame. Called by FurnitureManager.update().
   */
  update(deltaTime) {
    this._updateLegHeightTween(deltaTime);
    this._updateVariantTween(deltaTime);
  }

  isAnimating() {
    return this._tween !== null || this._legHeightTween !== null;
  }

  // Cleanup
  dispose() {
    // Clean up attachment points
    this.attachmentPoints.forEach((point) => point.dispose());
    this.attachmentPoints = [];

    // Detach from parent if attached
    this.detachFrom(this.scene);

    // Dispose attached children
    this.attachedChildren.forEach((child) => {
      child.detachFrom();
    });
    this.attachedChildren = [];

    this._cancelPendingFades();
    this._tween = null;
    this._legHeightTween = null;
    this._legGroups = null;
    this._animatedNodes.clear();

    disposeMesh(this.mesh);

    // Outline is managed centrally by FurnitureManager
  }

  // Essential getters

  getMesh() {
    return this.mesh;
  }
}

// Behaviour lives in ./element/*; `this` and every call site are unchanged.
installMethods(FurnitureElement, legHeightStatics, variantTweenStatics);
installMethods(
  FurnitureElement.prototype,
  moduleAdjacencyMethods,
  legHeightMethods,
  variantTweenMethods,
  variantSwitchMethods,
);
